/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDashboardsRequest,
  ListDashboardsSecurity,
} from "../models/operations/listdashboards.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDashboardsQuery,
  DashboardsQueryData,
  prefetchDashboards,
  queryKeyDashboards,
} from "./dashboards.core.js";
export {
  buildDashboardsQuery,
  type DashboardsQueryData,
  prefetchDashboards,
  queryKeyDashboards,
};

export type DashboardsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDashboards dashboards
 *
 * @remarks
 * List the project's dashboards, most recently updated first, each with its cards.
 */
export function useDashboards(
  request?: ListDashboardsRequest | undefined,
  security?: ListDashboardsSecurity | undefined,
  options?: QueryHookOptions<DashboardsQueryData, DashboardsQueryError>,
): UseQueryResult<DashboardsQueryData, DashboardsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildDashboardsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDashboards dashboards
 *
 * @remarks
 * List the project's dashboards, most recently updated first, each with its cards.
 */
export function useDashboardsSuspense(
  request?: ListDashboardsRequest | undefined,
  security?: ListDashboardsSecurity | undefined,
  options?: SuspenseQueryHookOptions<DashboardsQueryData, DashboardsQueryError>,
): UseSuspenseQueryResult<DashboardsQueryData, DashboardsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDashboardsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDashboardsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: DashboardsQueryData,
): DashboardsQueryData | undefined {
  const key = queryKeyDashboards(...queryKeyBase);

  return client.setQueryData<DashboardsQueryData>(key, data);
}

export function invalidateDashboards(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dashboards", "list", ...queryKeyBase],
  });
}

export function invalidateAllDashboards(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dashboards", "list"],
  });
}
